// The first part of the start-up (loaded by main.tsx before app-entry.tsx): the stored options, the game of this
// page, the background, and the language file. app-entry.tsx is imported only once `languageReady` has resolved,
// so its top level can use t() without a top-level await (owner 2026-10-25: Safari 14 and Chrome 83 do not know
// top-level await, the page stayed empty there).
import { env } from './env';
import { normalizeGameId } from './engine/rules';
import { RANKS } from './engine/cards';
import type { GameId } from './engine/types';
import { defaultOptions, readStoredOptions } from './options/storage';
import { backgroundSrc, setPictureFormats } from './options/assets';
import { loadLanguage, resolveLanguage } from './i18n';

/** The 52 card ids (rank + suit) of one deck, for the image preloads. */
export const CARD_IDS = RANKS.flatMap((r) => ['c', 'd', 'h', 's'].map((s) => r + s));

declare global {
  interface Window { __webpReady?: Promise<boolean>; __webp?: boolean; __bgWebp?: boolean; __noWebp?: boolean }
}

/** The background's format: the page template's choice (WebP when an earlier visit found it works), the same here, so
 *  it is downloaded once; the cards follow the test of this visit (`picturesReady` below). */
function backgroundWebp(): boolean {
  if (typeof window.__bgWebp === 'boolean') return window.__bgWebp;
  try {
    return localStorage.getItem('solitaire_storage:webp') === '1' && window.__noWebp !== true;
  } catch {
    return false;
  }
}
setPictureFormats(false, backgroundWebp());

export const isMobile =
  matchMedia('(pointer: coarse)').matches &&
  !matchMedia('(min-width: 1000px) and (min-height: 700px), (min-width: 700px) and (min-height: 1000px)').matches;
const defaults = defaultOptions({ mobile: isMobile, narrow: innerWidth <= 720, target: env.appTarget });
export const { options } = readStoredOptions(defaults);
/** The small picture of the table: a small phone under 2x (the same rule as the page template's head script). */
export const smallBackground = (): boolean => isMobile && innerWidth <= 750 && devicePixelRatio < 2;
export const setBackground = (bg: string) => {
  // The partner page keeps its own background whatever theme is saved (legacy TGT-005).
  const name = env.appTarget === 'zboze' ? 'classic_old_zboze' : bg;
  document.body.style.backgroundImage = `url("${backgroundSrc(name, smallBackground())}")`;
};
// At once, before the language file and the rest of the start (owner 2026-10-17: black for long); the page
// template's inline script has usually set the same URL already.
setBackground(options.skin.backgroundType);

/** `/#<gameId>` picks the game for this page (not saved); the hash is then cleared to `#`. */
function gameFromHash(): string | null {
  const h = location.hash.replace(/^#/, '');
  if (!h) return null;
  history.replaceState(null, '', location.pathname + location.search + '#'); // every hash is cleared, legacy #window-* too
  return h.startsWith('window-') ? null : h;
}
/** "12,3,..." from the /my-score/ page data, or null when missing or not a list of integers. */
export function sharedPattern(raw: string | undefined): number[] | null {
  if (!raw) return null;
  const p = raw.split(',').map((v) => Number(v.trim()));
  return p.length && p.every((n) => Number.isInteger(n) && n >= 0) ? p : null;
}
const hashGame = gameFromHash();
export const pageData = env.pageData;
export const game: GameId =
  pageData?.place_name === 'my-score'
    ? normalizeGameId(pageData.place_params.game_type)
    : hashGame
      ? normalizeGameId(hashGame)
      : options.option.game;

/** The page template's WebP test (a few ms; at most 2 s): the card pictures are named only after it, so a first visit
 *  gets WebP too. Without the template's script (a test page): PNG. */
const picturesReady: Promise<void> = (window.__webpReady ?? Promise.resolve(false)).then(
  (webp) => setPictureFormats(webp === true, backgroundWebp()),
  () => undefined,
);
export const languageReady: Promise<void> = Promise.all([loadLanguage(resolveLanguage(options.option.lang, navigator.language)), picturesReady]).then(() => undefined);
